import React, { useCallback, useEffect, useState } from "react";
import API from "../services/api";
import { localExpiry } from "../utils/accessManagement";
import "./AddServer.css";

const THEME_STORAGE_KEY = "plcGatewayHomeTheme";

const readPageTheme = () => {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved === "light" || saved === "dark") {
            return saved;
        }
    } catch (error) {
        console.error("Error reading theme preference:", error);
    }

    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }

    return "light";
};

const messageTone = (text) => {
    const value = String(text || "").toLowerCase();
    if (value.startsWith("failure") || value.includes("fail") || value.includes("please")) {
        return "error";
    }
    if (value.startsWith("testing")) {
        return "pending";
    }
    if (value.startsWith("success") || value.includes("successfully")) {
        return "success";
    }
    return "info";
};

const AddServer = ({ canCreate = true, embedded = false, isSuperadmin = false }) => {
    const theme = readPageTheme();
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [port, setPort] = useState(4840);
    const [description, setDescription] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [message, setMessage] = useState("");
    const [servers, setServers] = useState([]);
    const [testResult, setTestResult] = useState("");
    const [loadingServers, setLoadingServers] = useState(true);
    const [serversError, setServersError] = useState("");
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(null);

    const fetchServers = useCallback(async () => {
        try {
            const response = await API.get("/servers/");
            setServers(response.data);
            setServersError("");
        } catch (error) {
            console.error("Failed to fetch servers:", error);
            setServersError("Unable to load servers.");
        } finally {
            setLoadingServers(false);
        }
    }, []);

    useEffect(() => {
        fetchServers();
    }, [fetchServers]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await API.post("/servers/", {
                name,
                address,
                port,
                description,
                expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
            });
            setMessage("Server added successfully!");
            setName("");
            setAddress("");
            setPort(4840);
            setDescription("");
            setExpiresAt("");
            fetchServers();
        } catch (error) {
            setMessage("Failed to add server.");
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const beginEdit = (server) => {
        setEditingId(server.id);
        setEditForm({
            name: server.name || "",
            address: server.address || "",
            port: server.port || 4840,
            description: server.description || "",
            expires_at: localExpiry(server.expires_at),
        });
        setMessage("");
    };

    const updateEdit = (key, value) => setEditForm((current) => ({ ...current, [key]: value }));

    const saveEdit = async (server) => {
        setSaving(true);
        setMessage("");
        try {
            await API.patch(`/servers/${server.id}/`, {
                name: editForm.name,
                address: editForm.address,
                port: editForm.port,
                description: editForm.description,
            });
            await API.put(`/access/servers/${server.id}/lifecycle/`, {
                expires_at: editForm.expires_at ? new Date(editForm.expires_at).toISOString() : null,
                is_enabled: server.is_enabled !== false,
            });
            setEditingId(null);
            setEditForm(null);
            setMessage("Server settings saved successfully.");
            await fetchServers();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Failed to save server settings.");
        } finally {
            setSaving(false);
        }
    };

    const toggleArchive = async (server) => {
        const archive = server.is_enabled !== false;
        if (archive && !window.confirm("Archive this server? The collector will stop reading it, but the server, tags, and history will remain.")) return;
        setSaving(true);
        setMessage("");
        try {
            await API.put(`/access/servers/${server.id}/lifecycle/`, {
                expires_at: server.expires_at || null,
                is_enabled: !archive,
            });
            setMessage(archive ? "Server archived. Collector reads are stopped." : "Server restored. Collector reads can resume.");
            await fetchServers();
        } catch (error) {
            setMessage(error?.response?.data?.detail || (archive ? "Failed to archive server." : "Failed to restore server."));
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        setTestResult("Testing connection...");
        if (!address || !port) {
            setTestResult("Please provide both address and port.");
            return;
        }

        setTesting(true);
        try {
            const response = await API.post("/test-connection/", {
                address,
                port,
            });
            setTestResult(`Success: ${response.data.message}`);
        } catch (error) {
            setTestResult(`Failure: ${error.response?.data?.message || "Unable to connect"}`);
        } finally {
            setTesting(false);
        }
    };

    const serverRows = Array.isArray(servers) ? servers : [];
    const activeServerRows = serverRows.filter((server) => server.is_enabled !== false);
    const archivedServerRows = isSuperadmin ? serverRows.filter((server) => server.is_enabled === false) : [];

    const renderServerRow = (server) => (
        <li className="server-page__item" key={server.id}>
            {editingId === server.id && editForm ? (
                <div className="server-page__edit">
                    <div className="server-page__grid">
                        <div className="server-page__field"><label>Name</label><input value={editForm.name} onChange={(event) => updateEdit("name", event.target.value)} /></div>
                        <div className="server-page__field"><label>Address</label><input value={editForm.address} onChange={(event) => updateEdit("address", event.target.value)} /></div>
                        <div className="server-page__field"><label>Port</label><input type="number" value={editForm.port} onChange={(event) => updateEdit("port", event.target.value)} /></div>
                        <div className="server-page__field"><label>Expiry</label><input type="datetime-local" step="1" value={editForm.expires_at} onChange={(event) => updateEdit("expires_at", event.target.value)} /><small>{editForm.expires_at ? "Scheduled expiry" : "Unlimited"}</small></div>
                        <div className="server-page__field server-page__field--wide"><label>Description</label><textarea rows="3" value={editForm.description} onChange={(event) => updateEdit("description", event.target.value)} /></div>
                    </div>
                    <div className="server-page__actions">
                        <button type="button" className="server-page__button server-page__button--primary" disabled={saving} onClick={() => saveEdit(server)}>Save</button>
                        <button type="button" className="server-page__button" disabled={saving} onClick={() => { setEditingId(null); setEditForm(null); }}>Cancel</button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="server-page__summary">
                        <div>
                            <h3>{server.name}</h3>
                            <p className="server-page__endpoint">{server.address}:{server.port}</p>
                        </div>
                        <span className={`server-page__state${server.is_enabled === false ? " is-archived" : ""}`}>{server.is_enabled === false ? "Archived" : "Active"}</span>
                    </div>
                    <p className="server-page__description">{server.description || "No description"}</p>
                    <p className="server-page__expiry">Expiry: {server.expires_at ? localExpiry(server.expires_at).replace("T", " ") : "Unlimited"}</p>
                    {canCreate && <div className="server-page__row-actions">
                        <button type="button" className="server-page__button" onClick={() => beginEdit(server)}>Edit</button>
                        <button type="button" className={`server-page__button${server.is_enabled === false ? " server-page__button--primary" : " server-page__button--danger"}`} disabled={saving} onClick={() => toggleArchive(server)}>{server.is_enabled === false ? "Restore" : "Archive"}</button>
                    </div>}
                </>
            )}
        </li>
    );

    return (
        <div className={`server-page${embedded ? " server-page--embedded" : ""}`} data-theme={theme}>
            {!embedded ? <header className="server-page__top">
                <div>
                    <p className="server-page__eyebrow">OPC UA servers</p>
                    <h1>{canCreate ? "Add Server" : "Assigned servers"}</h1>
                    <p className="server-page__subtitle">
                        {canCreate ? "Save a server endpoint, or test the address and port when you choose to. Nothing connects on its own." : "Servers assigned to your administrator account."}
                    </p>
                </div>
            </header> : null}

            {canCreate && <section className="server-page__card" aria-labelledby="add-server-heading">
                <div className="server-page__card-head">
                    <h2 id="add-server-heading">Server details</h2>
                    <p>Port starts at 4840. A server is created only when you submit this form.</p>
                </div>

                <form className="server-page__form" onSubmit={handleSubmit}>
                    <div className="server-page__grid">
                        <div className="server-page__field">
                            <label htmlFor="server-name">Server Name</label>
                            <input
                                id="server-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="server-page__field">
                            <label htmlFor="server-address">Server Address</label>
                            <input
                                id="server-address"
                                type="text"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                required
                                spellCheck="false"
                                autoComplete="off"
                            />
                        </div>
                        <div className="server-page__field">
                            <label htmlFor="server-port">Server Port</label>
                            <input
                                id="server-port"
                                type="number"
                                value={port}
                                onChange={(e) => setPort(e.target.value)}
                                required
                            />
                        </div>
                        <div className="server-page__field server-page__field--wide">
                            <label htmlFor="server-description">Description</label>
                            <textarea
                                id="server-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                            />
                        </div>
                        <div className="server-page__field">
                            <label htmlFor="server-expiry">Expiry</label>
                            <input
                                id="server-expiry"
                                type="datetime-local"
                                step="1"
                                value={expiresAt}
                                onChange={(event) => setExpiresAt(event.target.value)}
                            />
                            <small>{expiresAt ? "Scheduled expiry" : "Unlimited"}</small>
                        </div>
                    </div>
                    <div className="server-page__actions">
                        <button className="server-page__button server-page__button--primary" type="submit" disabled={saving}>
                            {saving ? "Adding…" : "Add Server"}
                        </button>
                    </div>
                </form>

                {message && (
                    <p className={`server-page__banner is-${messageTone(message)}`} role="status">
                        {message}
                    </p>
                )}

                <div className="server-page__test">
                    <button
                        className="server-page__button"
                        type="button"
                        onClick={handleTestConnection}
                        disabled={testing}
                    >
                        {testing ? "Testing…" : "Test Connection"}
                    </button>
                    {testResult && (
                        <p className={`server-page__banner is-${messageTone(testResult)}`} role="status">
                            {testResult}
                        </p>
                    )}
                </div>
            </section>}

            <section className="server-page__card" aria-labelledby="existing-servers-heading">
                <div className="server-page__card-head">
                    <h2 id="existing-servers-heading">Existing Servers</h2>
                    <p>{loadingServers ? "Loading servers…" : `${activeServerRows.length} active`}</p>
                </div>

                {serversError && <p className="server-page__banner is-error" role="alert">{serversError}</p>}

                {loadingServers ? (
                    <p className="server-page__empty">Loading servers…</p>
                ) : activeServerRows.length === 0 ? (
                    <p className="server-page__empty">No servers are stored yet.</p>
                ) : (
                    <ul className="server-page__list">
                        {activeServerRows.map(renderServerRow)}
                    </ul>
                )}
            </section>

            {isSuperadmin ? <section className="server-page__card" aria-labelledby="archived-servers-heading">
                <div className="server-page__card-head">
                    <h2 id="archived-servers-heading">Archived Servers</h2>
                    <p>{loadingServers ? "Loading servers…" : `${archivedServerRows.length} archived`}</p>
                </div>
                {loadingServers ? <p className="server-page__empty">Loading servers…</p> : archivedServerRows.length === 0 ? (
                    <p className="server-page__empty">No archived servers.</p>
                ) : <div className="server-page__table-wrap"><table className="server-page__archive-table">
                    <thead><tr><th>Name</th><th>Endpoint</th><th>Expiry</th><th>Actions</th></tr></thead>
                    <tbody>{archivedServerRows.map((server) => (
                        <React.Fragment key={server.id}>
                            <tr>
                                <td>{server.name}</td>
                                <td className="server-page__endpoint">{server.address}:{server.port}</td>
                                <td>{server.expires_at ? localExpiry(server.expires_at).replace("T", " ") : "Unlimited"}</td>
                                <td><div className="server-page__row-actions">
                                    <button type="button" className="server-page__button" onClick={() => beginEdit(server)}>Edit</button>
                                    <button type="button" className="server-page__button server-page__button--primary" disabled={saving} onClick={() => toggleArchive(server)}>Restore</button>
                                </div></td>
                            </tr>
                            {editingId === server.id && editForm ? <tr><td colSpan="4">
                                <div className="server-page__edit">
                                    <div className="server-page__grid">
                                        <div className="server-page__field"><label>Name</label><input value={editForm.name} onChange={(event) => updateEdit("name", event.target.value)} /></div>
                                        <div className="server-page__field"><label>Address</label><input value={editForm.address} onChange={(event) => updateEdit("address", event.target.value)} /></div>
                                        <div className="server-page__field"><label>Port</label><input type="number" value={editForm.port} onChange={(event) => updateEdit("port", event.target.value)} /></div>
                                        <div className="server-page__field"><label>Expiry</label><input type="datetime-local" step="1" value={editForm.expires_at} onChange={(event) => updateEdit("expires_at", event.target.value)} /><small>{editForm.expires_at ? "Scheduled expiry" : "Unlimited"}</small></div>
                                        <div className="server-page__field server-page__field--wide"><label>Description</label><textarea rows="3" value={editForm.description} onChange={(event) => updateEdit("description", event.target.value)} /></div>
                                    </div>
                                    <div className="server-page__actions">
                                        <button type="button" className="server-page__button server-page__button--primary" disabled={saving} onClick={() => saveEdit(server)}>Save</button>
                                        <button type="button" className="server-page__button" disabled={saving} onClick={() => { setEditingId(null); setEditForm(null); }}>Cancel</button>
                                    </div>
                                </div>
                            </td></tr> : null}
                        </React.Fragment>
                    ))}</tbody>
                </table></div>}
            </section> : null}
        </div>
    );
};

export default AddServer;
